import { ButtonBase, CircularProgress, IconButton } from '@mui/material';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { FreeMode, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import BlocksView from './BlocksView';
import HomesView from './HomesView';
import StagesView from './StagesView';
import { useShowroom } from 'context';
import EntrancesView from './EntrancesView';

const ShaxmatkaVisualView = ({ toggleHomeItem = () => {} }) => {
  const { t } = useTranslation();
  const [, dispatch] = useShowroom();
  const [searchParams] = useSearchParams();
  const [prevObjectId, setPrevObjectId] = useState(
    searchParams.get('objectId'),
  );
  const [sliderActiveIndex, setSliderActiveIndex] = useState(0);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [selectedEntrance, setSelectedEntrance] = useState(null);
  const [step, setStep] = useState('block');
  const axiosPrivate = useAxiosPrivate();
  const swiperRef = useRef(null);
  // Avtomatik tanlash faqat bir marta — foydalanuvchi "ortga" bosganda
  // uni qaytarib olib kelmasligi uchun
  const autoPicked = useRef({ block: false, stage: false });

  // Filtrda tanlangan bloklar (manzildagi block[0], block[1] ...)
  const filteredBlockIds = useMemo(() => {
    const ids = [];
    searchParams.forEach((value, key) => {
      if (key.startsWith('block')) ids.push(String(value));
    });

    return ids;
  }, [searchParams]);

  const [blocksVisualQuery, stagesVisualQuery, homesVisualQuery] = useQueries([
    {
      queryKey: ['shaxmatkaBlocksVisualView', searchParams.get('objectId')],
      queryFn: async function () {
        const response = await axiosPrivate.get(
          `/admin/visual/object/${searchParams.get('objectId')}`,
        );
        return response.data.data;
      },
      enabled: Boolean(searchParams.get('objectId')),
      retry: false,
    },
    {
      queryKey: [
        'shaxmatkaStagesVisualView',
        selectedBlock?.id,
        selectedEntrance?.id,
      ],
      queryFn: async function () {
        if (selectedBlock?.id) {
          const response = await axiosPrivate.get(
            `/admin/visual/block/${selectedBlock?.id}${selectedEntrance?.id ? '?padezd_id=' + selectedEntrance?.id : ''}`,
          );
          return response.data.data;
        }
        return [];
      },
      // Diqqat: `step` ga bog'lanmaydi — blok tanlanishi bilan yuklanadi,
      // shunda "qavat chizmasi bormi" degan qaror tayyor natija ustida
      // qabul qilinadi (birinchi ochilishdagi poyga shundan kelib chiqqan)
      enabled: Boolean(selectedBlock?.id),
      retry: false,
    },
    {
      queryKey: [
        'shaxmatkaHomesVisualView',
        selectedStage,
        selectedEntrance?.id,
      ],
      queryFn: async function () {
        if (selectedBlock?.id && selectedStage) {
          const response = await axiosPrivate.get(
            `/admin/visual/homes/${selectedBlock?.id}?stage=${selectedStage}${selectedEntrance?.id ? '&padezd_id=' + selectedEntrance?.id : ''}`,
          );
          return response.data.data;
        }
        return [];
      },

      enabled: Boolean(selectedStage) && step == 'homes',
      retry: false,
    },
  ]);

  const hasEntrancePolygon = useMemo(() => {
    if (!blocksVisualQuery?.data?.blocks?.length || !selectedBlock?.id)
      return false;
    const block = blocksVisualQuery?.data?.blocks?.find(
      (block) => block?.id == selectedBlock?.id,
    );
    return block?.padezdimage;
  }, [blocksVisualQuery.data, selectedBlock?.id]);

  const [entrancesVisualQuery] = useQueries([
    {
      queryKey: [
        'shaxmatkaEntranceVisualView',
        selectedBlock?.id,
        hasEntrancePolygon,
      ],
      queryFn: async function () {
        if (selectedBlock?.id) {
          const response = await axiosPrivate.get(
            `/admin/visual/padezds/${selectedBlock?.id}`,
          );
          return response.data.data;
        }
        return [];
      },
      enabled: Boolean(selectedBlock?.id) && hasEntrancePolygon,
      retry: false,
    },
  ]);

  // Blokning barcha uylari (qavatsiz) — qavat chizmasi yo'q bo'lganda ishlatiladi
  const [allHomesVisualQuery] = useQueries([
    {
      queryKey: [
        'shaxmatkaAllHomesVisualView',
        selectedBlock?.id,
        selectedEntrance?.id,
      ],
      queryFn: async function () {
        if (selectedBlock?.id) {
          const response = await axiosPrivate.get(
            `/admin/visual/homes/${selectedBlock?.id}${selectedEntrance?.id ? '?padezd_id=' + selectedEntrance?.id : ''}`,
          );
          return response.data.data;
        }
        return [];
      },
      enabled: Boolean(selectedBlock?.id) && step == 'allhomes',
      retry: false,
    },
  ]);

  useEffect(() => {
    if (
      searchParams.get('objectId') &&
      searchParams.get('objectId') != prevObjectId
    ) {
      setPrevObjectId(searchParams.get('objectId'));
      blocksVisualQuery.refetch();
    }
  }, [searchParams]);

  // 1) Filtrda BITTA blok tanlangan bo'lsa — vizual ko'rinish o'sha blokni
  //    ochadi. Bir nechta blok tanlangan bo'lsa, qaysi birini ochishni
  //    bilib bo'lmaydi — bloklar ro'yxati ko'rsatiladi.
  //
  //    Blok obyekt planida chizilmagan bo'lsa ham (yoki obyekt plani umuman
  //    yo'q bo'lsa ham) baribir o'sha blokka o'tiladi: uning o'z qavat yoki
  //    xonadon chizmasi bo'lishi mumkin. Chizma topilmasa, quyidagi effektlar
  //    uni 'allhomes' ro'yxatiga olib tushadi.
  useEffect(() => {
    if (autoPicked.current.block || selectedBlock?.id) return;
    if (filteredBlockIds.length !== 1) return;

    // Obyekt so'rovi tugamaguncha qaror qilmaymiz — aks holda chizma
    // kelishidan oldin "topilmadi" deb noto'g'ri shoxga tushib qolamiz
    const settled =
      (blocksVisualQuery.isSuccess || blocksVisualQuery.isError) &&
      !blocksVisualQuery.isFetching;
    if (!settled) return;

    const match = blocksVisualQuery?.data?.blocks?.find(
      (block) => String(block?.id) === filteredBlockIds[0],
    );

    autoPicked.current.block = true;

    if (match) {
      setSelectedBlock({ id: match.id, name: match.block });
      setStep(match.padezdimage ? 'entrances' : 'stages');
    } else {
      setSelectedBlock({ id: Number(filteredBlockIds[0]), name: '' });
      setStep('stages');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    blocksVisualQuery.data,
    blocksVisualQuery.isSuccess,
    blocksVisualQuery.isError,
    blocksVisualQuery.isFetching,
    filteredBlockIds,
  ]);

  // 2) Qavat tanlanmagan va uy tafsiloti ochilmagan bo'lsa — birinchi
  //    CHIZILGAN qavat ochiladi. Backend faqat polygon chizilgan
  //    qavatlarni qaytaradi, shuning uchun ro'yxatdagi eng pasti olinadi.
  useEffect(() => {
    if (autoPicked.current.stage || selectedStage !== null) return;
    if (step !== 'stages') return;

    const stages = stagesVisualQuery?.data?.blocks;
    if (!stages?.length) return;

    const first = stages
      .map((item) => item?.stage)
      .filter((stage) => stage !== null && stage !== undefined)
      .sort((a, b) => Number(a) - Number(b))[0];

    if (first === undefined) return;

    autoPicked.current.stage = true;
    setSelectedStage(first);
    setStep('homes');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stagesVisualQuery?.data, step]);

  // Qavat so'rovi tugadimi. queryFn blok tanlanmaganda [] qaytaradi —
  // bu "chizma yo'q" degani emas, shuning uchun massiv ham tugamagan deb
  // hisoblanadi. Xatolik (chizma umuman yo'q) esa tugagan natija.
  const stagesHasDrawing = Boolean(stagesVisualQuery.data?.id);
  // Qavat RASMI bo'lmasa ham qavatlar ro'yxati kelishi mumkin
  // (`{id: null, blocks: [{stage: -2}, {stage: -1}]}`). Bunday holatda
  // qavat tanlanib xonadonlar planiga o'tiladi — zaxira ro'yxat kerak emas.
  const stagesHasList = Boolean(stagesVisualQuery.data?.blocks?.length);
  const stagesSettled =
    !stagesVisualQuery.isIdle &&
    !stagesVisualQuery.isLoading &&
    !stagesVisualQuery.isFetching &&
    !Array.isArray(stagesVisualQuery.data);

  // Qavat chizmasi yo'q bo'lsa — qavat bosqichini o'tkazib, blok uylariga
  // o'tamiz. Teskarisi ham: chizma keyinroq kelsa, ro'yxatdan qaytamiz.
  useEffect(() => {
    if (!selectedBlock?.id) return;

    // Qavat allaqachon tanlangan bo'lsa — qaror qilingan, aralashmaymiz.
    // (Qavat tanlash effekti shu commitda ishlagan bo'lishi mumkin, bu
    //  yerdagi `step` esa hali eski qiymatni ko'radi.)
    if (selectedStage !== null) return;

    const hasAnything = stagesHasDrawing || stagesHasList;

    if (step === 'stages' && stagesSettled && !hasAnything) {
      setStep('allhomes');
    } else if (step === 'allhomes' && hasAnything) {
      setStep('stages');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    step,
    stagesSettled,
    stagesHasDrawing,
    stagesHasList,
    selectedStage,
    selectedBlock?.id,
  ]);

  // Blok filtri o'zgarsa — avtomatik tanlash qaytadan ishlashi kerak
  useEffect(() => {
    autoPicked.current = { block: false, stage: false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredBlockIds.join(',')]);

  const handleBackFromStages = () => {
    // Blokdan chiqildi — blok ham, qavat ham qayta tanlanmasin
    autoPicked.current = { block: true, stage: true };
    dispatch({ type: 'setHomeId', payload: null });
    setStep(hasEntrancePolygon ? 'entrances' : 'blocks');
  };

  const handleBackFromHomes = () => {
    // Foydalanuvchi ataylab qaytdi — qavatni qayta avtomatik tanlamaymiz
    autoPicked.current.stage = true;
    setSelectedStage(null);
    dispatch({ type: 'setHomeId', payload: null });
    setStep('stages');
  };

  return (
    <div className='sheet-wrapper !h-full'>
      <div className='sheet-base-area relative !h-full'>
        {blocksVisualQuery.isLoading ||
        blocksVisualQuery.isFetching ||
        entrancesVisualQuery.isLoading ||
        entrancesVisualQuery.isFetching ||
        stagesVisualQuery.isLoading ||
        stagesVisualQuery.isFetching ||
        allHomesVisualQuery.isLoading ||
        allHomesVisualQuery.isFetching ? (
          <div className='circular-progress-box min-h-[500px] h-full w-full'>
            <CircularProgress size={50} />
          </div>
        ) : (
          <>
            {step == 'allhomes' ? (
              <div className='shaxmatka-visual-view-homes-wrapper !h-full'>
                <div style={{ padding: 12 }}>
                  <ButtonBase
                    className='stage-btn'
                    onClick={() => {
                      setSelectedStage(null);
                      setStep(hasEntrancePolygon ? 'entrances' : 'blocks');
                    }}
                  >
                    <i className='bi bi-chevron-left' />
                  </ButtonBase>
                </div>
                {allHomesVisualQuery.data?.homes?.length ? (
                  <div
                    className='visual-fallback-list'
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fill, minmax(90px, 1fr))',
                      gap: 12,
                      padding: 12,
                    }}
                  >
                    {allHomesVisualQuery.data.homes.map((item, index) => (
                      <ButtonBase
                        key={`allhome-${index}`}
                        className={`stage-btn status-${item?.homes?.status}`}
                        onClick={() => {
                          // Uy tanlanganda qavati ham o'sha uydan olinadi
                          setSelectedStage(item?.homes?.stage ?? null);
                          dispatch({
                            type: 'setHomeId',
                            payload: item?.homes?.id,
                          });
                        }}
                      >
                        №{item?.homes?.number}
                        <small style={{ display: 'block' }}>
                          {item?.homes?.stage}-qavat
                        </small>
                      </ButtonBase>
                    ))}
                  </div>
                ) : (
                  <div className='shaxmatka-visual-view-wrapper !h-full'>
                    <span className='no-data-found-wrapper h-full'>
                      <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                      {t('common.global.noDataFound')}
                    </span>
                  </div>
                )}
              </div>
            ) : step == 'homes' ? (
              <div className='shaxmatka-visual-view-homes-wrapper !h-full'>
                <div className='stages-swiper-wrapper !h-full'>
                  <div className='stages-swiper-body !h-[calc(100%-20px)]'>
                    <Swiper
                      lazy='true'
                      direction='vertical'
                      freeMode={true}
                      slidesPerView={8}
                      spaceBetween={12}
                      pagination={false}
                      modules={[FreeMode, Pagination]}
                      onBeforeInit={(swiper) => {
                        swiperRef.current = swiper;
                      }}
                      onSlideChange={(a) => {
                        setSliderActiveIndex(a?.activeIndex);
                      }}
                      className='h-full !pb-2 !pt-1'
                    >
                      {stagesVisualQuery.data.blocks &&
                        stagesVisualQuery.data.blocks.length > 0 &&
                        stagesVisualQuery.data.blocks.map((block, index) => (
                          <SwiperSlide
                            key={`swiper-item-${index}`}
                            className='min-h-[60px]'
                          >
                            <div className='swiper-stage-item'>
                              <ButtonBase
                                className={`stage-btn${
                                  selectedStage == block?.stage
                                    ? ' is-selected'
                                    : ''
                                }`}
                                onClick={() => {
                                  setSelectedStage(block?.stage);
                                  toggleHomeItem(null, null);
                                }}
                              >
                                {block?.stage}
                              </ButtonBase>
                            </div>
                          </SwiperSlide>
                        ))}
                    </Swiper>
                    <div
                      className={`swiper-navigation-button-wrapper prev${
                        sliderActiveIndex == 0 ? ' is-disabled' : ''
                      }`}
                    >
                      <IconButton
                        variant='swiperNavigationButton'
                        color='primary'
                        onClick={() => {
                          swiperRef.current?.slidePrev();
                        }}
                        disabled={sliderActiveIndex == 0}
                        size='small'
                      >
                        <i className='bi bi-chevron-left' />
                      </IconButton>
                    </div>
                    <div
                      className={`swiper-navigation-button-wrapper next${
                        sliderActiveIndex ==
                        stagesVisualQuery.data.blocks.length - 1
                          ? ' is-disabled'
                          : ''
                      }`}
                    >
                      <IconButton
                        variant='swiperNavigationButton'
                        color='primary'
                        onClick={() => {
                          swiperRef.current?.slideNext();
                        }}
                        disabled={
                          sliderActiveIndex ==
                          stagesVisualQuery.data.blocks.length - 1
                        }
                        size='small'
                      >
                        <i className='bi bi-chevron-right' />
                      </IconButton>
                    </div>
                  </div>
                </div>
                <div className='homes-view-wrapper'>
                  {homesVisualQuery.isLoading || homesVisualQuery.isFetching ? (
                    <div className='circular-progress-box min-h-[500px] h-full w-full'>
                      <CircularProgress size={35} />
                    </div>
                  ) : homesVisualQuery.data &&
                    !homesVisualQuery.data?.id &&
                    homesVisualQuery.data?.homes?.length ? (
                    // Rasm chizmasi yo'q — uylar ro'yxati tugma ko'rinishida chiqadi
                    <div
                      className='visual-fallback-list'
                      style={{
                        display: 'grid',
                        gridTemplateColumns:
                          'repeat(auto-fill, minmax(90px, 1fr))',
                        gap: 12,
                        padding: 12,
                      }}
                    >
                      {homesVisualQuery.data.homes.map((item, index) => (
                        <ButtonBase
                          key={`fallback-home-${index}`}
                          className={`stage-btn status-${item?.homes?.status}`}
                          onClick={() =>
                            dispatch({
                              type: 'setHomeId',
                              payload: item?.homes?.id,
                            })
                          }
                        >
                          №{item?.homes?.number}
                        </ButtonBase>
                      ))}
                    </div>
                  ) : homesVisualQuery.data && homesVisualQuery.data?.id ? (
                    <HomesView
                      data={homesVisualQuery.data}
                      stagesData={stagesVisualQuery.data}
                      backFn={handleBackFromHomes}
                      selectedBlockId={selectedBlock}
                      selectedStage={selectedStage}
                      onClickPolygonFn={(homeId, selectedBId, ...prp) => {
                        // toggleHomeItem(homeId, selectedBId, ...prp);
                        dispatch({ type: 'setHomeId', payload: homeId });
                      }}
                    />
                  ) : (
                    <div className='shaxmatka-visual-view-wrapper !h-full'>
                      <span className='no-data-found-wrapper h-full'>
                        <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                        {t('common.global.noDataFound')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : step == 'stages' ? (
              stagesVisualQuery.data && stagesVisualQuery.data?.id ? (
                <StagesView
                  data={stagesVisualQuery.data}
                  selectedStage={selectedStage}
                  selectedBlock={selectedBlock}
                  selectedEntrance={selectedEntrance}
                  backFn={handleBackFromStages}
                  onClickPolygonFn={(value) => {
                    setSelectedStage(value);
                    setStep('homes');
                    toggleHomeItem(null, null);
                  }}
                />
              ) : (
                <div className='shaxmatka-visual-view-wrapper !h-full'>
                  <span className='no-data-found-wrapper h-full'>
                    <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                    {t('common.global.noDataFound')}
                  </span>
                </div>
              )
            ) : step == 'entrances' ? (
              entrancesVisualQuery.data && entrancesVisualQuery.data?.id ? (
                <EntrancesView
                  data={entrancesVisualQuery.data}
                  selectedStage={selectedStage}
                  selectedBlock={selectedBlock}
                  backFn={() => {
                    setSelectedEntrance(null);
                    setStep('blocks');
                  }}
                  onClickPolygonFn={(value) => {
                    setSelectedEntrance({
                      id: value?.id,
                      name: value?.padezd,
                    });
                    setStep('stages');
                  }}
                />
              ) : (
                <div className='shaxmatka-visual-view-wrapper !h-full'>
                  <span className='no-data-found-wrapper h-full'>
                    <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                    {t('common.global.noDataFound')}
                  </span>
                </div>
              )
            ) : blocksVisualQuery.data && blocksVisualQuery.data?.id ? (
              <BlocksView
                data={blocksVisualQuery.data}
                selectedBlockId={selectedBlock?.id}
                onClickPolygonFn={(value) => {
                  setSelectedBlock({ id: value?.id, name: value?.block });
                  setStep(value?.padezdimage ? 'entrances' : 'stages');
                }}
              />
            ) : (
              <div className='shaxmatka-visual-view-wrapper !h-full'>
                <span className='no-data-found-wrapper h-full'>
                  <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                  {t('common.global.noDataFound')}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default memo(ShaxmatkaVisualView);
